import { Head, Link, router, useForm } from "@inertiajs/react";
import { useState } from "react";
import type { FormEvent } from "react";
import { Layout } from "@/components/Layout";

type Invite = {
  id: string;
  label: string | null;
  phone: string | null;
  url: string;
  state: "open" | "claimed" | "expired";
  plan: string | null;
  claimedBy: string | null;
  expiresAt: string | null;
};

type Plan = { id: number; name: string; describe: string };

export default function Invites(
  { invites, plans, defaultPlanId }:
  { invites: Invite[]; host: string; plans: Plan[]; defaultPlanId: number | null },
) {
  const { data, setData, post, processing } = useForm({
    label: "", phone: "", plan_id: defaultPlanId ?? plans[0]?.id ?? 0,
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    post("/invites", { onSuccess: () => setData({ label: "", phone: "" }) });
  }

  return (
    <Layout title="Invites">
      <Head title="Invites" />

      <form onSubmit={submit} className="mb-8 max-w-lg space-y-4">
        <label className="block">
          <span className="text-sm font-medium">Who is it for?</span>
          <input
            type="text" value={data.label} placeholder="Sam"
            onChange={(e) => setData("label", e.target.value)}
            className={inputClass}
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium">Their mobile number (optional)</span>
          <input
            type="tel" inputMode="tel" value={data.phone} placeholder="+447700900123"
            onChange={(e) => setData("phone", e.target.value)}
            className={inputClass}
          />
          <span className="mt-1 block text-xs text-neutral-500">
            Pins the link to that number, so a forwarded link cannot be used by anyone
            else. Leave blank for a link anyone can claim once.
          </span>
        </label>

        {/* Chosen here rather than adjusted later, so somebody arrives on the
            tier they were offered. */}
        <label className="block">
          <span className="text-sm font-medium">What are they allowed?</span>
          <select
            value={data.plan_id}
            onChange={(e) => setData("plan_id", Number(e.target.value))}
            className={inputClass}
          >
            {plans.map((plan) => (
              <option key={plan.id} value={plan.id}>{plan.name} — {plan.describe}</option>
            ))}
          </select>
        </label>

        <button
          type="submit" disabled={processing}
          className="w-full rounded-lg bg-brand-600 px-4 py-2.5 font-medium text-white transition hover:bg-brand-700 disabled:opacity-50"
        >
          {processing ? "Creating…" : "Create invite"}
        </button>
      </form>

      {invites.length === 0 ? (
        <p className="rounded-lg border border-dashed border-neutral-300 px-4 py-8 text-center text-sm text-neutral-500 dark:border-neutral-700">
          No invites yet.
        </p>
      ) : (
        <ul className="space-y-2">
          {invites.map((invite) => <Row key={invite.id} invite={invite} />)}
        </ul>
      )}
    </Layout>
  );
}

function Row({ invite }: { invite: Invite }) {
  const [copied, setCopied] = useState(false);

  // Copying beats selecting a long token by hand on a phone, which is the
  // whole reason this page exists.
  function copy() {
    navigator.clipboard?.writeText(invite.url).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 2000); },
      () => {},
    );
  }

  return (
    <li className="rounded-lg border border-neutral-200 bg-white px-4 py-3 dark:border-neutral-800 dark:bg-neutral-900">
      <div className="flex items-center justify-between gap-3">
        <span className="font-medium">
          {invite.label || "Anyone"}
          {invite.phone && <span className="ml-2 text-sm font-normal text-neutral-500">{invite.phone}</span>}
          {invite.plan && <span className="ml-2 text-sm font-normal text-neutral-500">· {invite.plan}</span>}
        </span>
        <Badge state={invite.state}>
          {invite.state === "claimed" ? `claimed by ${invite.claimedBy}` : invite.state}
        </Badge>
      </div>

      {invite.state === "open" && (
        <>
          <div className="mt-2 break-all rounded bg-neutral-50 px-2 py-1 font-mono text-xs text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
            {invite.url}
          </div>
          <div className="mt-2 flex gap-4 text-sm">
            <button type="button" onClick={copy} className="text-brand-700 hover:underline dark:text-brand-400">
              {copied ? "copied" : "copy link"}
            </button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`Court Pilot books David Lloyd courts the moment they open. Your invite: ${invite.url}`)}`}
              target="_blank" rel="noreferrer"
              className="text-neutral-500 hover:underline"
            >
              send on WhatsApp
            </a>
            <button
              type="button"
              onClick={() => { if (confirm("Revoke this invite?")) router.delete(`/invites/${invite.id}`); }}
              className="text-red-600 hover:underline dark:text-red-400"
            >
              revoke
            </button>
          </div>
          {invite.expiresAt && (
            <p className="mt-1 text-xs text-neutral-400">Expires {invite.expiresAt}</p>
          )}
        </>
      )}
    </li>
  );
}

function Badge({ state, children }: { state: Invite["state"]; children: string }) {
  const tones = {
    open: "bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-200",
    claimed: "bg-brand-50 text-brand-700 dark:bg-brand-700/20 dark:text-brand-50",
    expired: "bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400",
  };

  return (
    <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${tones[state]}`}>
      {children}
    </span>
  );
}

const inputClass =
  "mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-neutral-700 dark:bg-neutral-900";
